import { type FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TeamMemberTransitionDisabledReason } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Loader from 'components/Loader';
import Stepper from 'components/Stepper';
import { StepOptions } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import { MemberRoleTransitionRouteParams } from 'features/Team/routes';

import { useMemberRoleTransitionState } from '../MemberRoleTransitionProvider';
import { useTeamMember } from './hooks/useTeamMember';
import { CannotEditTeamMember } from './steps/CannotEditTeamMember';
import { MemberRole } from './steps/MemberRole';
import { Review } from './steps/Review';
import { getBestEditableRole } from './utils';

type CanEditRole = {
  can: boolean;
  reason?: TeamMemberTransitionDisabledReason;
};

export const MemberRoleTransitionStepper: FC = () => {
  const { companyUserId } = useParams<MemberRoleTransitionRouteParams>();
  const { companyProfileId } = useCompanyContext();
  const { dispatch, identity } = useMemberRoleTransitionState();
  const [canEditRole, setCanEditRole] = useState<CanEditRole>({ can: true });

  const { called, loading, teamMember } = useTeamMember(
    companyProfileId,
    companyUserId,
  );

  useEffect(() => {
    if (teamMember) {
      const allDisabled = teamMember.transitions.every(
        ({ isDisabled }) => isDisabled,
      );

      if (allDisabled) {
        setCanEditRole({
          can: false,
          reason: teamMember.transitions[0]?.disabledReason ?? undefined,
        });
        return;
      }

      const bestRole = getBestEditableRole(teamMember.roles ?? []);

      if (!bestRole) {
        setCanEditRole({ can: false });
        return;
      }

      dispatch({
        payload: {
          cuid: teamMember.companyUserOrInvitationId,
          email: teamMember.email,
          firstName: teamMember.firstName,
          lastName: teamMember.lastName,
          role: bestRole,
        },
        type: 'INITIALIZE_TEAM_MEMBER_INFO',
      });
      dispatch({
        payload: teamMember.transitions,
        type: 'INITIALIZE_TRANSITIONS',
      });
    }
  }, [teamMember, companyUserId, dispatch]);

  const isLoading = !identity || loading || !called;

  const steps: StepOptions[] = [
    {
      key: 'member-role',
      render: (props) => (
        <>
          <TrackView pageName="MemberRoleTransitionRole" />
          <MemberRole {...props} />
        </>
      ),
    },
    {
      key: 'review',
      render: (props) => (
        <>
          <TrackView pageName="MemberRoleTransitionReview" />
          <Review {...props} />
        </>
      ),
    },
  ];

  if (!canEditRole.can) {
    return <CannotEditTeamMember reason={canEditRole.reason} />;
  }
  if (isLoading) {
    return <Loader />;
  }

  return <Stepper steps={steps} />;
};
